import { isEmpty, isEqual, isNil, isNull } from "lodash";
import {
  ConfirmationStoreInfo,
  CSDict,
  IndividualHandlingConfirmationTargetRate,
  IndividualHandlingSearchRequest,
  MerchantAddress,
  ConfirmationMerchantInfoItem,
} from "../../features/api-interfaces";
import {
  BLANK_LABEL_VALUE,
  COMMON_LABELS,
  getDivisionLabel,
} from "../default-constants";
import {
  ADDRESS_APP,
  ADDRESS_NG,
  ADDRESS_TAX,
  ADDRESS_TSR,
  RESULT_NG,
  RESULT_OK,
  DEFAULT_COMMENT,
  LABELS,
  REQUIRES_ADDRESS_CONFIRMATION,
  REQUIRED_REPRESENTATIVE_CONFIRMATION,
  STATUS_CONFIRMING,
  STATUS_CONFIRMED,
  EXISTS,
  NOT_EXIST,
} from "./constants";
import { changeStyle } from "../../utils/format";

/**
 * 個別取扱事由のラベルを以下のように返却する.
 * 検索条件指定あり：検索条件の個別取扱事由
 * 検索条件指定なし：表示したいリストの個別取扱事由の/区切り
 * @param {CSDict[] | undefined} iHRCodeList コードリストの個別取扱事由
 * @param {string[] | undefined} iHRList 表示したい個別取扱事由コードリスト
 * @param {string} iHRSearchCondition 検索条件の個別取扱事由
 * @returns {string} 個別取扱事由のラベル
 */
export const getIndividualHandlingReasonLabel = (
  iHRCodeList: CSDict[] | undefined,
  iHRList: string[] | undefined,
  iHRSearchCondition: string = ""
): string => {
  if (isNil(iHRCodeList)) {
    return "";
  }
  if (isEmpty(iHRSearchCondition)) {
    return iHRList!
      .map((item) => getDivisionLabel(iHRCodeList, item))
      .join(" / ");
  } else {
    return getDivisionLabel(iHRCodeList, iHRSearchCondition);
  }
};

/**
 * 画面表示用の確認結果を返却する.
 * @param {boolean | null} confirmationResult 確認結果
 * @returns {CSDict} 画面表示用の確認結果
 */
export const getConfirmationResultDict = (
  confirmationResult: boolean | null
): CSDict => {
  if (isNil(confirmationResult)) {
    return BLANK_LABEL_VALUE;
  }

  return confirmationResult ? RESULT_OK : RESULT_NG;
};

/**
 * 確認結果欄に表示する住所種別のラベルを返却する.
 * @param {number} addressType 対象の種別
 * @returns {string} 確認結果欄に表示する住所種別のラベル
 */
export const getConfirmationAddressLabel = (
  confirmationResult: boolean | null,
  addressType: number
): string => {
  if (confirmationResult === false) {
    // 確認結果NGは「NG」を返却する
    return ADDRESS_NG.label;
  }

  switch (String(addressType)) {
    case ADDRESS_TSR.value:
      return ADDRESS_TSR.label;
    case ADDRESS_TAX.value:
      return ADDRESS_TAX.label;
    case ADDRESS_APP.value:
      return ADDRESS_APP.label;
    default:
      return COMMON_LABELS.BLANK;
  }
};

/**
 * 引数で受け取った確認結果のvalue値からAPI返却用の確認結果（true | false | null）を返却する.
 * @param {string} confirmationResultValue 確認結果のvalue値
 * @returns {boolean | null} API返却用の確認結果（true | false | null）
 */
export const getApiConfirmationResult = (
  confirmationResultValue: string
): boolean | null => {
  if (isEqual(confirmationResultValue, BLANK_LABEL_VALUE.value)) {
    return null;
  }

  return isEqual(confirmationResultValue, RESULT_OK.value);
};

/**
 * 引数で受け取った住所選択のvalue値からAPI返却用の確認結果（true | false | null）を返却する.
 * @param {number} addressTypeValue 住所選択のvalue値
 * @returns {boolean | null} API返却用の確認結果（true | false | null）
 */
export const getApiConfirmationResultForAddress = (
  addressTypeValue: number
): boolean | null => {
  if (addressTypeValue === 0) {
    return null;
  }

  return !isEqual(addressTypeValue, Number(ADDRESS_NG.value));
};

/**
 * 確認結果をもとにコメントを返却する.
 * 確認NGであればデフォルトコメントを返却する.
 * @param {string | undefined} confirmationResult 確認結果value
 * @param {string | null | undefined} comment コメント
 * @returns {string} コメント
 */
export const getComment = (
  confirmationResult: string | null | undefined,
  comment?: string | null
) => {
  if (isEqual(confirmationResult, RESULT_NG.value) && isEmpty(comment)) {
    return DEFAULT_COMMENT;
  }

  return comment ?? "";
};

/**
 * 引数で受け取った住所選択のvalue値をもとにコメントを返却する.
 * 確認NGであればデフォルトコメントを返却する.
 * @param {number} addressTypeValue 住所選択のvalue値
 * @param {string} comment コメント
 * @returns {string} コメント
 */
export const getAddressComment = (
  addressTypeValue: number,
  comment: string
) => {
  if (isEqual(addressTypeValue, Number(ADDRESS_NG.value)) && isEmpty(comment)) {
    return DEFAULT_COMMENT;
  }

  return comment ?? "";
};

/**
 * APIから取得した料率カテゴリ一覧を区分連想配列に変換する.
 * @param {IndividualHandlingConfirmationTargetRate[] | undefined} targetRates
 * @returns {CSDict[]} 変換した料率カテゴリ一覧
 */
export const getTargetRateItems = (
  targetRates: IndividualHandlingConfirmationTargetRate[] | undefined
): CSDict[] => {
  if (targetRates !== undefined) {
    return targetRates.map((targetRates) => ({
      label: targetRates.categoryName,
      value: String(targetRates.categoryNo),
    }));
  }
  return [];
};

/**
 * 画面表示用の料率カテゴリコードを返却する.
 * @param {CSDict[]} rateCategoryItems 料率カテゴリ一覧
 * @param {number | null} destinationSpecificationCode APIから受け取った料率カテゴリコード
 * @returns {CSDict} 画面表示用の料率カテゴリコード
 */
export const getRateCategoryCodeDict = (
  rateCategoryItems: CSDict[],
  rateCategoryCode: number | null
) => {
  if (isNil(rateCategoryCode )) {
    return BLANK_LABEL_VALUE;
  }

  const rateCategory = rateCategoryItems.find((item) =>
    isEqual(item.value, String(rateCategoryCode))
  );
  return rateCategory ?? BLANK_LABEL_VALUE;
};

/**
 * 個別取扱確認結果一覧画面の以下の検索条件に絞り込んだ店舗一覧を返却する
 * ・個別取扱事由
 * ・ステータス
 * @param {ConfirmationStoreInfo[] | undefined} storeList
 * @param {IndividualHandlingSearchRequest} searchCriteria
 * @return {ExConfirmationStoreInfo[] | undefined}
 */
export const filteredStoreList = (
  storeList: ConfirmationStoreInfo[] | undefined,
  searchCriteria: IndividualHandlingSearchRequest
): ConfirmationStoreInfo[] | undefined => {
  const { individualHandlingReason, status } = searchCriteria;

  return storeList?.filter((store) => {
    let matchesReason = true;
    let matchesStatus = true;
    if (!isEmpty(individualHandlingReason)) {
      matchesReason = isEqual(
        store.individualHandlingReason,
        individualHandlingReason
      );
    }
    if (!isEmpty(status)) {
      if (isEqual(status, STATUS_CONFIRMING.value)) {
        matchesStatus = isNull(store.confirmationResult);
      } else if (isEqual(status, STATUS_CONFIRMED.value)) {
        matchesStatus = !isNull(store.confirmationResult);
      }
    }
    return matchesReason && matchesStatus;
  });
};

/**
 * 要代表者確認の個別取扱事由が含まれているか判定する.
 * @param {string} individualHandlingReason 個別取扱事由
 * @returns {boolean} 要代表者確認が含まれているかどうか
 */
export const isRequiresRepNameConfirm = (
  individualHandlingReason: string
): boolean => {
  if (isEmpty(individualHandlingReason)) {
    return false;
  }

  return individualHandlingReason
    .split(",")
    .includes(REQUIRED_REPRESENTATIVE_CONFIRMATION);
};

/**
 * 事業者住所確認の個別取扱事由が含まれているか判定する.
 * @param {string} individualHandlingReason 個別取扱事由
 * @returns {boolean} 事業者住所確認が含まれているかどうか
 */
export const isRequiresAddressConfirm = (
  individualHandlingReason: string
): boolean => {
  if (isEmpty(individualHandlingReason)) {
    return false;
  }

  return individualHandlingReason
    .split(",")
    .includes(REQUIRES_ADDRESS_CONFIRMATION);
};

/**
 * 引数で受け取った住所種別の住所情報を返却する.
 *
 * ただし、以下の場合はundefinedを返す.
 * ・住所情報リストに対象の住所種別がない.
 * ・郵便番号と住所1が取得できていない.
 * @param {MerchantAddress} addressList 住所情報リスト
 * @param {number} addressType 抽出する住所種別
 * @returns {MerchantAddress | undefined} 抽出した住所情報
 */
export const getAddress = (
  addressList: MerchantAddress[] | null | undefined,
  addressType: number
): MerchantAddress | undefined => {
  const address = addressList?.find((address) =>
    isEqual(address.addressType, addressType)
  );

  if (isEmpty(address?.postalCode) && isEmpty(address?.address1)) {
    return undefined;
  }

  return address;
};

/**
 * 引数で受け取った住所情報を画面表示用の住所（〒{郵便番号} / {住所}）の文字列に整形する.
 * ただし、null値であればハイフンを返す.
 *
 * @param {MerchantAddress | undefined} address APIから受け取った住所情報
 * @returns {string} 画面表示用（〒{郵便番号} / {住所}）の文字列
 */
export const formatAddress = (address: MerchantAddress | undefined): string => {
  if (!address) {
    return COMMON_LABELS.HYPHEN;
  }

  if (isEmpty(address.address1) && isEmpty(address.address2)) {
    return COMMON_LABELS.HYPHEN;
  }

  const postalCode = changeStyle(address.postalCode);
  const address1 = address.address1 ?? "";
  const address2 = address.address2 ?? "";
  const addressStr = address1 + address2;

  return postalCode + LABELS.SLASH + addressStr;
};

/**
 * 以下のkey情報を基に受け取った店舗情報の一致判定を行う.
 * ・申請ステータス番号
 * ・個別取扱事由
 * @param {ConfirmationStoreInfo} storeList 店舗情報
 * @param {number} applicationStatusNo 申請ステータス番号
 * @param {string} individualHandlingReason 個別取扱事由
 * @returns {boolean} true: 同一店舗情報である
 */
export const isSameStore = (
  store: ConfirmationStoreInfo,
  applicationStatusNo: number,
  individualHandlingReason: string
): boolean =>
  isEqual(store.applicationStatusNo, applicationStatusNo) &&
  isEqual(store.individualHandlingReason, individualHandlingReason);

/**
 * 引数で受け取った情報を基に確認結果が登録済みの店舗の個別取扱情報を返却する
 * @param {ConfirmationStoreInfo[] | undefined | null} storeList 店舗情報一覧
 * @param {number} applicationStatusNo 申請ステータス番号
 * @param {string} individualHandlingReason 個別取扱事由
 * @returns {ConfirmationStoreInfo | undefined} 店舗情報
 */
export const findRegisteredStore = (
  storeList: ConfirmationStoreInfo[] | undefined | null,
  applicationStatusNo: number,
  individualHandlingReason: string
): ConfirmationStoreInfo | undefined =>
  storeList?.find(
    (store) =>
      isSameStore(store, applicationStatusNo, individualHandlingReason) &&
      !isNull(store.confirmationResult)
  );

/**
 * 引数で受け取った住所種別から住所選択のボタン用ラベルを返却する.
 * @param {number} addressType 住所種別
 * @returns {string} 住所選択のボタン用ラベル
 */
export const getAddressButtonLabel = (addressType: number): string => {
  if (isEqual(ADDRESS_TSR.value, String(addressType))) {
    return LABELS.UPDATE_TSR_BUTTON_NAME;
  } else if (isEqual(ADDRESS_TAX.value, String(addressType))) {
    return LABELS.UPDATE_TAX_BUTTON_NAME;
  } else if (isEqual(ADDRESS_APP.value, String(addressType))) {
    return LABELS.UPDATE_MERCHANT_BUTTON_NAME;
  } else if (isEqual(ADDRESS_NG.value, String(addressType))) {
    return LABELS.UPDATE_NG_BUTTON_NAME;
  } else {
    return COMMON_LABELS.BLANK;
  }
};

/**
 * 個別取扱事由コードに対応するデータを返却する.
 * @param {string} reason 個別取扱事由コード
 * @param {ConfirmationMerchantInfo} merchant 加盟店情報
 * @returns {ConfirmationMerchantInfoItem} 加盟店情報アイテム
 */
export const getIndividualHandlingReason = (
  reason: string,
  merchantInfoItems: ConfirmationMerchantInfoItem[]
): ConfirmationMerchantInfoItem | undefined => {
  const merchantInfoItem = merchantInfoItems.find((item) =>
    isEqual(item.individualHandlingReason, reason)
  );

  return merchantInfoItem;
};

/**
 * 画面表示用の確認結果を返却する.
 * @param {boolean | null} confirmationResult 確認結果
 * @returns {CSDict} 画面表示用の確認結果
 */
export const getDetailDisplayDict = (
  detailDisplayFlag: boolean | null
): CSDict => {
  if (isNil(detailDisplayFlag)) {
    return BLANK_LABEL_VALUE;
  }

  return detailDisplayFlag ? EXISTS : NOT_EXIST;
};

/**
 * 明細詳細反映有無の確認結果を返却する.
 * @param {boolean | null} detailDisplayFlagValue 確認結果
 * @returns {CSDict} 明細詳細反映有無の確認結果
 */
export const getDetailDisplay = (
  detailDisplayFlagValue: string
): boolean | null => {
  if (isEqual(detailDisplayFlagValue, BLANK_LABEL_VALUE.value)) {
    return null;
  }

  return isEqual(detailDisplayFlagValue, EXISTS.value);
};
